import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan } from '@fortawesome/pro-light-svg-icons/faBan';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import type { ReviewMessage } from '../outreachTypes';
import { ReviewEditor } from './ReviewEditor';
import type { HintLine } from './reviewHints';
import { angleText, factTexts } from './reviewLabels';
import { reviewMessages as messages } from './reviewMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Review.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReviewPitchCardProps {
  accountId: number;
  campaignUuid: string;
  message: ReviewMessage;
  hints: HintLine[];
  isEditing: boolean;
  onEditDone: () => void;
  onNever: () => void;
  disabled: boolean;
}

/**
 * The open pitch (design outreach-v2 h1, right): the person (the name shows here because the artist
 * reads the pitch before it goes), "Never", the angle, "Passes the rules" or the hints, the text
 * and the facts that it uses. Edit swaps the text for the editor.
 */
const ReviewPitchCardComponent: React.FC<ReviewPitchCardProps> = ({
  accountId,
  campaignUuid,
  message,
  hints,
  isEditing,
  onEditDone,
  onNever,
  disabled,
}) => {
  const intl = useIntl();
  const facts = useMemo(() => factTexts(intl, message.factsUsed), [intl, message.factsUsed]);
  const hintRows = useMemo(
    () =>
      hints.map((hint) => (
        <div key={hint.key} className={clsx(shared.hint, hint.blocks && shared.hintBlock)}>
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>{hint.text}</span>
        </div>
      )),
    [hints]
  );
  return (
    <>
      <section className={styles.card} data-testid="review-person">
        <div className={styles.person}>
          <div className={styles.personText}>
            <h3 className={styles.personTitle}>{message.targetName ?? message.contactName}</h3>
            {message.targetName !== null && (
              <p className={`${shared.small} m-0`}>{intl.formatMessage(messages.to, { name: message.contactName })}</p>
            )}
          </div>
          <SMLButton size="sm" color="link" omitCap leftIcon={faBan} onClick={onNever} disabled={disabled}>
            {intl.formatMessage(messages.never)}
          </SMLButton>
        </div>
      </section>
      <section className={styles.card} data-testid="review-card">
        <div className={styles.cardHead}>
          <h3 className={styles.cardTitle}>{intl.formatMessage(messages.thePitch)}</h3>
          <div className={shared.row}>
            {message.step > 1 && <span className={shared.chip}>{intl.formatMessage(messages.followUp)}</span>}
            <span className={clsx(shared.chip, shared.chipVio)}>
              {intl.formatMessage(messages.angle, { angle: angleText(intl, message.angle) })}
            </span>
            {hints.length === 0 && (
              <span className={clsx(shared.chip, shared.chipPos)}>
                <FontAwesomeIcon icon={faCheck} /> {intl.formatMessage(messages.passes)}
              </span>
            )}
          </div>
        </div>
        {hintRows}
        {isEditing ? (
          <ReviewEditor accountId={accountId} campaignUuid={campaignUuid} message={message} onDone={onEditDone} />
        ) : (
          <>
            <div className={shared.msgSubject}>{message.subject}</div>
            <p className={shared.msgBody}>{message.body}</p>
            {facts.length > 0 && (
              <span className={`${shared.chip} align-self-start`}>
                {intl.formatMessage(messages.uses, { facts: facts.join(' · ') })}
              </span>
            )}
          </>
        )}
      </section>
    </>
  );
};

export const ReviewPitchCard = TypedMemo(ReviewPitchCardComponent);
